import {
  useCallback,
  useEffect,
  useRef,
  type PointerEventHandler,
  type PointerEvent as ReactPointerEvent,
} from "react";

export interface UseLongPressOptions {
  /**
   * Hold duration in ms before firing. Per Apple HIG quick-action window.
   * Default: 250 (per `mobile.html §6` + `mockup/MOBILE.md §6`).
   */
  thresholdMs?: number;
  /** Pointer movement in px that cancels the long-press. Default: 10. */
  moveTolerancePx?: number;
  /**
   * If true, fire `navigator.vibrate(12)` on long-press trigger (per
   * `mobile.html §6` + `mobile-touch` skill).  Default: true.
   */
  haptic?: boolean;
  /** Called once when the long-press fires. */
  onLongPress: (event: ReactPointerEvent<HTMLElement>) => void;
  /**
   * Called when the gesture is cancelled (pointer up before threshold,
   * pointer moved beyond tolerance, pointer cancelled, or component unmount).
   */
  onCancel?: () => void;
  /** Disabled flag — if true, the gesture does nothing. */
  disabled?: boolean;
}

/**
 * useLongPress — pointer-event-based long-press hook.
 *
 * Per `mockup/MOBILE.md §6`:
 *   - Trigger time **250 ms** (not 500 ms — HIG quick-action window).
 *   - pointermove > 10 px cancels (avoids finger micro-jitter false-cancel).
 *   - haptic `vibrate(12)` on fire.
 *   - After fire, the `click` and `contextmenu` that the press is about to
 *     produce are swallowed (see `armSwallow` below).
 *
 * Pointer Events (not touchstart/mousedown) so it works on:
 *   - touch (iOS Safari, Android Chrome)
 *   - mouse (desktop dev)
 *   - pen (rare)
 *
 * Returns **a single `onPointerDown` handler**. Pass it as a prop:
 *
 *     <button onPointerDown={longPress} …>
 *
 * …and **not** `{...longPress}`. W39.93 measured what that does: a spread of a
 * function copies its own *enumerable* properties, and a function has none, so
 * the spread evaluates to `{}` and no handler is attached at all. That is
 * exactly what `LayoutEditor.tsx` did, so the mobile block-reorder sheet could
 * not be opened by any gesture — a real press-and-hold on a block handle
 * produced nothing, while the hook it called sat right there looking correct.
 * If a bundle of handlers is wanted later, return an object; do not document a
 * spread of a function.
 */
export function useLongPress({
  thresholdMs = 250,
  moveTolerancePx = 10,
  haptic = true,
  onLongPress,
  onCancel,
  disabled = false,
}: UseLongPressOptions): PointerEventHandler<HTMLElement> {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const startXRef = useRef<number | null>(null);
  const startYRef = useRef<number | null>(null);
  const firedRef = useRef(false);
  const armedRef = useRef<((ev: Event) => void) | null>(null);
  // Latest callbacks ref — keep stable so handler identity doesn't churn.
  const handlersRef = useRef({ onLongPress, onCancel });
  handlersRef.current = { onLongPress, onCancel };

  const clear = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    startXRef.current = null;
    startYRef.current = null;
  }, []);

  /**
   * Eat the `click` / `contextmenu` this press is about to cause.
   *
   * `preventDefault()` on the `pointerdown` cannot do this: a `click` is a
   * separate event dispatched afterwards, and on every surface this hook is
   * used from the element that took the press is *also* the thing that
   * navigates or opens a lightbox. Without the swallow, wiring the hook up
   * would make the product worse than leaving it unwired — the sheet would
   * open *and* the tap would still navigate.
   *
   * The first version scoped the swallow to the pressed element, and that was
   * wrong in the one case that matters: **when the gesture works.** The sheet
   * opens under the finger, so by the time the click is hit-tested its target
   * is the sheet's own backdrop, not the element that was pressed — the scope
   * check failed, the click went through, and `ActionSheet`'s
   * `onClick={onClose}` backdrop shut the sheet the user had just opened.
   * Measured at 393×852: open at 281 ms, gone by 659 ms, finger still down.
   *
   * So the next click is swallowed **regardless of target**, which is correct
   * because the next click after a long press *is* that press's release. The
   * listener removes itself when it fires, and `clearSwallow` on the next
   * `pointerdown` bounds the case where no click ever comes (the finger
   * scrolled away) — so a stale listener can never eat a later unrelated tap.
   */
  const clearSwallow = useCallback(() => {
    const armed = armedRef.current;
    if (!armed) return;
    window.removeEventListener("click", armed, { capture: true } as EventListenerOptions);
    window.removeEventListener("contextmenu", armed, { capture: true } as EventListenerOptions);
    armedRef.current = null;
  }, []);

  const armSwallow = useCallback(() => {
    clearSwallow();
    const swallow = (ev: Event) => {
      if (!firedRef.current) return;
      firedRef.current = false;
      clearSwallow();
      ev.preventDefault();
      ev.stopPropagation();
    };
    armedRef.current = swallow;
    window.addEventListener("click", swallow, { capture: true } as EventListenerOptions);
    window.addEventListener("contextmenu", swallow, { capture: true } as EventListenerOptions);
  }, [clearSwallow]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      const armed = armedRef.current;
      if (armed) {
        window.removeEventListener("click", armed, { capture: true } as EventListenerOptions);
        window.removeEventListener("contextmenu", armed, { capture: true } as EventListenerOptions);
        armedRef.current = null;
      }
    };
  }, []);

  return useCallback<PointerEventHandler<HTMLElement>>(
    (event) => {
      if (disabled) return;
      // Only primary button / single touch.
      if (event.pointerType === "mouse" && event.button !== 0) return;
      if (event.isPrimary === false) return;

      // A press that produced no click (the finger scrolled away instead) must
      // not leave the previous press's swallow armed for this one.
      clearSwallow();
      firedRef.current = false;
      startXRef.current = event.clientX;
      startYRef.current = event.clientY;

      const startX = startXRef.current;
      const startY = startYRef.current;

      const onMove = (ev: PointerEvent) => {
        if (startX == null || startY == null) return;
        if (firedRef.current) return;
        const dx = ev.clientX - startX;
        const dy = ev.clientY - startY;
        if (Math.hypot(dx, dy) > moveTolerancePx) {
          clear();
          window.removeEventListener("pointermove", onMove);
          window.removeEventListener("pointerup", onUp);
          window.removeEventListener("pointercancel", onCancelEvt);
          handlersRef.current.onCancel?.();
        }
      };

      const onUp = () => {
        // Released before threshold → cancel.
        if (!firedRef.current) {
          clear();
          handlersRef.current.onCancel?.();
        }
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onCancelEvt);
      };

      const onCancelEvt = () => {
        clear();
        handlersRef.current.onCancel?.();
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onCancelEvt);
      };

      timerRef.current = setTimeout(() => {
        firedRef.current = true;
        if (haptic && typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
          // Guarded because a throw here would skip `onLongPress` entirely and
          // the gesture would silently do nothing on exactly the devices that
          // vibrate. The sheet-local hook this one replaced carried this guard
          // (and the Safari note) for the whole time the canonical one did not.
          try {
            navigator.vibrate(12);
          } catch {
            /* Safari throws on some versions — silent, as it was there */
          }
        }
        armSwallow();
        handlersRef.current.onLongPress(event);
        // Tear down listeners — gesture done.
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onCancelEvt);
      }, thresholdMs);

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onCancelEvt);
    },
    [thresholdMs, moveTolerancePx, haptic, disabled, clear, armSwallow, clearSwallow],
  );
}
